// Deep link into a store's theme editor with our app embed block pre-activated
// (Shopify theme app extension "Deep linking", App embed block URL):
//
//   https://<shop>/admin/themes/current/editor?context=apps&template=index&activateAppId=<client_id>/cart-sync
//
// The merchant clicks it, reviews the toggle and saves. We never edit the theme
// ourselves (App Store requirement 5.1.1 forbids it), and the link plus the
// card's instructions cover the onboarding guidance of 5.1.3.

// The Liquid file name, without extension, of the app embed block
// (extensions/audiohook-theme/blocks/cart-sync.liquid in shopify-sales-integration).
// This is NOT the extension's handle (audiohook-cart-sync).
export const CART_SYNC_BLOCK_HANDLE = "cart-sync";

// Only a single-label *.myshopify.com host is accepted, so nothing else (extra
// labels, ports, paths, userinfo, a scheme) can be interpolated into an admin URL.
const SHOP_PATTERN = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/;
// The client id comes from the environment, so it is validated as a plain token.
const TOKEN_PATTERN = /^[A-Za-z0-9_-]+$/;

/**
 * Returns the deep link, or undefined when it cannot be built safely (client id
 * unset or malformed, shop missing or not a plain myshopify.com host).
 *
 * The slash before the handle is literal, as in Shopify's docs. URLSearchParams
 * would percent-encode it to %2F, so the string is assembled by hand from
 * values validated above.
 */
export function buildThemeEmbedUrl({ shop, clientId }) {
  if (typeof shop !== "string" || typeof clientId !== "string") return undefined;

  const host = shop.toLowerCase();
  // Trimmed: a value pasted into a hosting dashboard often carries a trailing
  // newline, which would otherwise hide the link with no visible cause.
  const id = clientId.trim();
  if (!SHOP_PATTERN.test(host) || !TOKEN_PATTERN.test(id)) return undefined;

  return `https://${host}/admin/themes/current/editor?context=apps&template=index&activateAppId=${id}/${CART_SYNC_BLOCK_HANDLE}`;
}

/**
 * How long a stored observation is trusted without asking the integrations
 * function again. Mirrors THEME_EMBED_CACHE_MS in
 * supabase/functions/integrations/index.ts: the two must change together, or the
 * client would skip requests the server would answer live (or the reverse).
 */
export const THEME_EMBED_CACHE_MS = 5 * 60 * 1000;

/**
 * The "delay note" sentence is suppressed only when the answer was observed this
 * recently, so a just-read "off" shows no note while an "off" that could not be
 * re-read does. Mirrors THEME_EMBED_BURST_MS in
 * supabase/functions/integrations/index.ts: within this window the server will
 * not read again, so a returning merchant must be told the answer may lag. One
 * window for both, change them together.
 */
export const THEME_EMBED_NOTE_QUIET_MS = 15 * 1000;

/** True when `checkedAt` parses, is not in the future and is under `windowMs` old. */
export function isCheckedWithin(checkedAt, nowMs, windowMs) {
  const checked = Date.parse(checkedAt);
  if (Number.isNaN(checked)) return false;
  const age = nowMs - checked;
  return age >= 0 && age < windowMs;
}

/**
 * True when asking the integrations function again cannot change anything: the
 * stored answer is "on" (only a positive answer is cached, because an "off" may
 * be stale within seconds when the merchant turns the embed on and comes back),
 * checked inside the cache window, and newer than `connected_at`. A missing or
 * unparsable timestamp (either one) means "not fresh". Same rule as the
 * server's decideSkip "cached" case.
 */
export function isThemeEmbedFresh(connection, nowMs) {
  if (connection?.theme_embed_enabled !== true) return false;
  const connected = Date.parse(connection.connected_at);
  if (Number.isNaN(connected)) return false;
  return isCheckedWithin(connection.theme_embed_checked_at, nowMs, THEME_EMBED_CACHE_MS)
    && Date.parse(connection.theme_embed_checked_at) > connected;
}
